import { MediaStatus, useCreateMutation, useUpdateMutation } from '../../graphql';
import { useConfig } from '../../hooks';
import { getFileDataFromUrl, getMediaTypeByExt } from '../mediaUtils';
import { MediaItem, MediaUploadArgs } from '../types';

type BaseHookArgs = {
  args: MediaUploadArgs;
  upload: (
    mediaItem: MediaItem,
    file?: File,
    fileUrl?: string
  ) => Promise<void>;
};

export const useMediaUpload = (args: BaseHookArgs) => {
  const { data } = useConfig();

  const { mutateAsync } = useCreateMutation({
    query: CREATE_MEDIA,
    modelName: 'media',
  });
  
  const { mutateAsync: updateMedia } = useUpdateMutation({
    query: UPDATE_MEDIA,
    modelName: 'media',
  });

  const uploadMedia = async (
    fileUrl: string,
    workspaceId?: string,
    file?: File,
    name?: string,
    categories?: Array<{ id: string }>
  ): Promise<string | undefined> => {
    if (!data || !fileUrl) return;

    const mediaItem = createMediaItem(fileUrl);
    
    // Override the name if provided
    if (name) {
      mediaItem.name = name;
    }

    const input: any = {
      name: mediaItem.name,
      status: MediaStatus.Pending, // Start with Pending status
      type: mediaItem.type,
      extension: mediaItem.extension,
    };

    if (workspaceId) {
      input.workspaces = [{ id: workspaceId }];
    }

    if (categories) {
      input.categories = categories;
    }


    const created = await mutateAsync({ input });
    mediaItem.id = created.id;
    // fire and forget heavy upload to keep UI responsive
    void args.upload(mediaItem, file, fileUrl);
    return created.id;
  };

  return uploadMedia;
};

const createMediaItem = (fileUrl: string): MediaItem => {
  const fileData = getFileDataFromUrl(fileUrl);
  return {
    id: '',
    name: fileData.name,
    url: fileUrl,
    extension: fileData.ext,
    type: getMediaTypeByExt(fileData.ext),
    status: MediaStatus.Pending,
  };
};

const CREATE_MEDIA = `
  mutation Mutation($input: CreateMediaInput!) {
    createMedia(input: $input) {
      id
    }
  }
`;

const UPDATE_MEDIA = `
  mutation Mutation($input: MutateMediaInput!) {
    updateMedia(input: $input) {
      id
    }
  }
`;
